import type { ChangeEvent } from 'react';
import { Clock } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

import type { QuestionRendererProps } from '../types';

export const TimeQuestion = ({
  question,
  value,
  onChange,
  onBlur,
  error,
  mode,
  disabled,
}: QuestionRendererProps<string>) => {
  if (question.type !== 'time') {
    return null;
  }

  const resolveTime = (candidate?: string) => {
    if (!candidate) return undefined;
    if (/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(candidate)) return candidate;
    return undefined;
  };

  const isInteractive = mode === 'runtime' && !disabled;
  const placeholder = question.placeholder || 'HH:MM';

  const minTime = resolveTime(question.minTime);
  const maxTime = resolveTime(question.maxTime);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!isInteractive) {
      return;
    }
    onChange?.(event.target.value);
  };

  const handleNowClick = () => {
    if (!isInteractive) {
      return;
    }
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    onChange?.(`${hours}:${minutes}`);
    onBlur?.();
  };

  const displayValue = resolveTime(value) ?? '';

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            type="time"
            value={displayValue}
            onChange={handleChange}
            onBlur={() => onBlur?.()}
            placeholder={placeholder}
            disabled={!isInteractive}
            min={minTime}
            max={maxTime}
            aria-invalid={Boolean(error)}
            aria-describedby={`validation-${question.id}`}
            aria-errormessage={error ? `error-${question.id}` : undefined}
            className="pr-10"
          />
          <Clock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        </div>
        {isInteractive && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleNowClick}
            className="whitespace-nowrap"
          >
            Now
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {minTime && maxTime
          ? `Select a time between ${minTime} and ${maxTime}`
          : minTime
            ? `Select a time on or after ${minTime}`
            : maxTime
              ? `Select a time on or before ${maxTime}`
              : 'Select a time'}
      </p>
    </div>
  );
};
